export interface Project {
  title: string
  description: string
  technologies: string[]
  github?: string
  live?: string
  image?: string
  categories: string[]
  highlights:string[]
  features:string[]
  type: 'personal' | 'client'
  client?: string // Company/client name for client projects
}

export interface ProjectsData {
  title: string
  subtitle: string
  description: string
  categories: string[]
  projects: Project[]
  callToAction: {
    text: string
    buttonText: string
    href: string
  }
}

export const projectsData: ProjectsData = {
  title: "Featured",
  subtitle: "Projects",
  description: "A showcase of my work in full-stack development and DevOps. Each project demonstrates my expertise in building scalable, secure, and efficient applications.",
  categories: ["All","Frontend", "Backend", "DevOps", "Infrastructure", "Automation", "Cloud", "Tools"],
  projects: [
    {
      title: "VidKit - Ultimate Video Toolkit",
      description: "A comprehensive web-based video editing platform that provides users with powerful tools for video compression and video-to-audio conversion. Built with a modern React frontend and integrated with Cloudinary for seamless video processing, VidKit offers an intuitive interface that eliminates the need for advanced technical skills while delivering professional-quality results.",
      technologies: [
        "React",
        "TypeScript", 
        "Vite",
        "Tailwind CSS",
        "Material UI",
        "Cloudinary API",
        "Formik",
        "React Dropzone",
        "React Toastify"
      ],
      github: "https://github.com/Huangdi-599/VidKit",
      live: "https://vidkit-omega.vercel.app/",
      image: "/vidkit.png",
      categories: [
        "Frontend"  
      ],
      type: "personal",
      highlights: [
        "Built a complete video processing web application with React and TypeScript",
        "Integrated Cloudinary API for serverless video compression and conversion",
        "Implemented drag-and-drop file upload with React Dropzone",
        "Created responsive design with Tailwind CSS and Material UI components",
        "Added real-time progress tracking and toast notifications for user feedback",
        "Developed modular component architecture for maintainable codebase"
      ],
      features: [
        "Video compression with quality optimization",
        "Video-to-audio conversion (MP4 to MP3)",
        "Drag-and-drop file upload interface",
        "Multiple resolution options (240p to 1080p)",
        "Real-time processing progress indicators",
        "Dark/light mode toggle",
        "Responsive design for all device sizes",
        "File size validation (up to 1GB)",
        "Instant download of processed files",
        "Toast notifications for user feedback"
      ]
    },
    {
      title: "Voxlio Voyage",
      description: "A comprehensive task completion and reward platform that allows users to earn instant cash by completing social media tasks, with integrated payment processing and real-time wallet management.",
      technologies: [
        "React",
        "TypeScript", 
        "Vite",
        "Tailwind CSS",
        "Ant Design",
        "Node.js",
        "Express.js",
        "MongoDB",
        "Mongoose",
        "JWT Authentication",
        "Twitter OAuth",
        "Flutterwave Payment API",
        "Nodemailer",
        "React Query",
        "React Router",
        "Formik",
        "Yup Validation"
      ],
      type:'client',
      client: "Voxlio",
      live: "https://voxlio.app",
      image: "/voxlio.png",
      categories: [
        'Frontend',
        'Backend',
        'DevOps',
      ],
      highlights: [
        "Built a complete task completion and reward platform with real-time payment processing",
        "Integrated Twitter OAuth for seamless user authentication and social media task verification",
        "Implemented secure wallet system with Flutterwave payment gateway for instant withdrawals",
        "Developed responsive UI with modern design using Tailwind CSS and Ant Design",
        "Created admin dashboard for task management and user oversight",
        "Built with TypeScript for type safety and better development experience"
      ],
      features: [
        "User authentication via Twitter OAuth integration",
        "Task management system with real-time assignment and completion tracking",
        "Digital wallet with balance tracking and transaction history",
        "Instant withdrawal system using Flutterwave payment gateway",
        "Admin dashboard for task creation, user management, and analytics",
        "Responsive design optimized for mobile and desktop devices",
        "Real-time notifications and status updates",
        "Secure API endpoints with JWT authentication",
        "Email notification system for important account activities",
        "Social media task verification and reward distribution"
      ]
    },
    {
      title: "E-Commerce Microservices Platform",
      description: "Enterprise-grade microservices architecture with 9 independent services including authentication, product management, order processing, payment integration, search engine, and real-time notifications. Features API Gateway with circuit breakers, rate limiting, comprehensive monitoring with Prometheus/Grafana, and full Kubernetes deployment with auto-scaling.",
      technologies: [
        "TypeScript", 
        "Express.js", 
        "Node.js", 
        "MongoDB Atlas", 
        "Redis", 
        "Elasticsearch", 
        "Docker", 
        "Kubernetes", 
        "AWS EKS", 
        "Prometheus", 
        "Grafana", 
        "JWT", 
        "Stripe API", 
        "Cloudinary", 
        "Nodemailer", 
        "BullMQ", 
        "NGINX", 
        "Helm Charts", 
        "GitHub Actions CI/CD"
      ],
      github: "https://github.com/Huangdi-599/ecommerce-microservices-ts",
      // live: "https://api.yourdomain.com",
      // image: "",
      categories: ["Backend", "DevOps"],
      type: "personal",
      highlights: [
        "API Gateway with Circuit Breakers & Rate Limiting",
        "9 Independent Microservices Architecture",
        "Production-Ready Kubernetes Deployment",
        "Comprehensive Monitoring & Observability",
        "Real-time Search with Elasticsearch",
        "Payment Processing with Stripe Integration",
        "Auto-scaling & Load Balancing",
        "TypeScript with Full Type Safety",
        "Docker Containerization",
        "CI/CD Pipeline with GitHub Actions"
      ],
      features: [
        "API Gateway with Centralized Routing",
        "Circuit Breakers for Service Resilience",
        "Rate Limiting & DDoS Protection",
        "User Authentication & Authorization (JWT)",
        "Product Management with Image Uploads",
        "Shopping Cart & Order Processing",
        "Payment Integration (Stripe)",
        "Email Notifications System",
        "Product Reviews & Ratings",
        "Advanced Search with Elasticsearch",
        "Multi-carrier Shipping Integration",
        "Redis Caching & Session Management",
        "Health Checks & Graceful Shutdown",
        "Role-based Access Control",
        "API Documentation with Swagger",
        "Database Sharding & Replication",
        "Message Queuing with Redis",
        "SSL/TLS with cert-manager"
      ]
    },
    {
      title: "MeshSpace - Social Media Platform",
      description: "Full-stack social media application with real-time features. Built with React/TypeScript frontend and Node.js/Express backend. Features include user authentication with email verification, JWT tokens, user profiles with avatar uploads, post creation with image support, trending/following feeds, threaded comments with replies and mentions, real-time notifications via Socket.io, search functionality, and responsive UI with dark mode. All media uploads use Cloudinary with file validation, and the platform includes forgot/reset password flows with email service integration.",
      technologies: ["React", "TypeScript", "Node.js", "Express", "MongoDB", "Socket.io", "JWT", "Cloudinary", "Multer", "Nodemailer", "Tailwind CSS", "shadcn/ui", "React Query", "Axios", "Vite"],
      github: "https://github.com/Huangdi-599/Mesh-Space",
      image: "/meshSpace.png",
      live: "https://mesh-space.vercel.app/",
      categories: ["Frontend", "Backend"],
      type: "personal",
      highlights: [
        "Implemented robust authentication flow with long-lived tokens",
        "Built real-time notification system using Socket.io",
        "Integrated Cloudinary for all media uploads with proper validation",
        "Created trending algorithm for post discovery",
        "Designed responsive UI with modern design system",
        "Implemented optimistic UI updates for better UX",
        "Added comprehensive error handling and logging"
      ],
      features: [
        "User authentication with email verification and JWT tokens",
        "Profile management with Cloudinary avatar uploads",
        "Post creation with image support and repost functionality",
        "Trending and following feeds with real-time updates",
        "Threaded comments with replies and mentions",
        "Real-time notifications via Socket.io",
        "Search functionality for users and posts",
        "Forgot/reset password with email service",
        "Responsive UI with dark mode and toast notifications",
        "File validation (2MB limit, image-only) for all uploads"
      ]
    },
    {
      title: "SQL to MongoDB Aggregation Pipeline",
      description: "A Python-based tool that translates SQL queries into MongoDB aggregation pipelines, allowing developers familiar with SQL to interact with MongoDB databases using a familiar query language. The tool leverages sqlglot library to parse SQL queries and dynamically construct corresponding MongoDB aggregation stages.",
      technologies: [
        "Python",
        "MongoDB",
        "SQL",
        "sqlglot",
        "pymongo",
        "CLI Tools",
        "Aggregation Pipelines",
        "AST Parsing"
      ],
      github: "https://github.com/Huangdi-599/sql_mongo_pipeline",
      // website: "https://pypi.org/project/sql-mongo-pipeline/",
      live: "https://github.com/Huangdi-599/sql_mongo_pipeline/actions",
      // image: "/images/sql-mongo-pipeline.png", // You'll need to add this image
      categories: ["Tools"],
      type: "personal",
      highlights: [
        "Automatically converts SQL queries to MongoDB aggregation pipelines",
        "Supports complex SQL features including CTEs, JOINs, GROUP BY, and ORDER BY",
        "Command-line interface for easy integration into workflows",
        "Published on PyPI with MIT license",
        "Comprehensive test coverage with pytest and mongomock",
        "Extensible modular design for adding new SQL features"
      ],
      features: [
        "SQL to MongoDB Aggregation conversion",
        "CTE (Common Table Expression) support with temporary collections",
        "LEFT JOIN operations via $lookup stages",
        "WHERE clause filtering with $match stages",
        "GROUP BY with aggregate functions (SUM, AVG, etc.)",
        "ORDER BY sorting with $sort stages",
        "LIMIT clause with $limit stages",
        "Command-line interface with multiple execution modes",
        "Pretty-printed JSON output",
        "Direct MongoDB execution with results display",
        "File-based SQL query processing",
        "Temporary collection management for CTEs"
      ]
    },
    {
      title: "ASETT - Crypto Portfolio Management Platform API",
      description: "A comprehensive cryptocurrency portfolio management web application that allows users to track, manage, and analyze their crypto investments in real-time. Built with Django REST Framework and integrated with CoinGecko API for live market data.",
      technologies: [
        "Django",
        "Django REST Framework", 
        "Python",
        "PostgreSQL",
        "JWT Authentication",
        "CoinGecko API",
        "HTML/CSS",
        "JavaScript",
        "Bootstrap",
        "APScheduler"
        ],
      github: "https://github.com/Huangdi-599/ASETT",
      // live: "https://your-deployed-app.com",
      // image: "/images/aset-crypto-portfolio.png",
      categories: [
        "Backend"
      ],
      type: "personal",
      highlights: [
        "Real-time cryptocurrency price tracking with 24-hour market data",
        "Secure JWT-based user authentication system",
        "Multi-portfolio management with detailed analytics",
        "Live integration with CoinGecko API for accurate market data",
        "Responsive web interface with search and pagination",
        "Password reset functionality with email verification",
        "Test-driven development approach with 80%+ test coverage"
      ],
      features: [
        "User Authentication & Authorization",
        "Portfolio Creation & Management", 
        "Cryptocurrency Addition/Removal",
        "Real-time Price Updates",
        "Portfolio Value Calculation",
        "Market Data Display",
        "Search & Filter Functionality",
        "Pagination for Large Datasets",
        "Password Reset System",
        "RESTful API Architecture",
        "Database Management",
        "Responsive UI Design"
      ]
    },

  ],
  callToAction: {
    text: "Interested in working together? Let's discuss your project needs.",
    buttonText: "Start a Project",
    href: "#contact"
  }
} 